import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  GUEST_ORDER_TEMPLATE_TYPE,
  upsertDispatchSettings,
  getPaymentMethods,
  getCompanySystemTemplate,
  upsertCompanySystemTemplate,
  deleteCompanySystemTemplate,
} from "../services/companySettingsService";
import type {
  UpsertDispatchSettingsRequest,
  UpsertCompanySystemTemplateRequest,
} from "../types/company.types";

// --- 부가정보(ul_company_dispatch) ---

export const useUpsertDispatchSettings = (companyGuid: string | null) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: UpsertDispatchSettingsRequest) =>
      upsertDispatchSettings(req),
    onSuccess: () => {
      toast.success("프로덕트 설정이 저장되었습니다.");
      qc.invalidateQueries({ queryKey: ["dispatchCompany", companyGuid] });
    },
    onError: (err: Error) => {
      toast.error(`저장 실패: ${err.message}`);
    },
  });
};

/** 결제타입 목록 (ul_sys_item). 시스템 공통값이라 업체와 무관하게 캐시 공유 */
export const usePaymentMethods = () =>
  useQuery({
    queryKey: ["paymentMethods"],
    queryFn: getPaymentMethods,
    staleTime: 30 * 60 * 1000,
  });

// --- 게스트 SMS 템플릿 ---

export const useCompanyGuestTemplate = (companyGuid: string | null) =>
  useQuery({
    queryKey: ["companySystemTemplate", companyGuid, GUEST_ORDER_TEMPLATE_TYPE],
    queryFn: () =>
      getCompanySystemTemplate(companyGuid!, GUEST_ORDER_TEMPLATE_TYPE),
    enabled: !!companyGuid,
    gcTime: 0,
  });

export const useUpsertGuestTemplate = (companyGuid: string | null) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: UpsertCompanySystemTemplateRequest) =>
      upsertCompanySystemTemplate(req),
    onSuccess: () => {
      toast.success("업체 전용 템플릿이 저장되었습니다.");
      qc.invalidateQueries({ queryKey: ["companySystemTemplate", companyGuid] });
    },
    onError: (err: Error) => {
      toast.error(`저장 실패: ${err.message}`);
    },
  });
};

export const useDeleteGuestTemplate = (companyGuid: string | null) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      deleteCompanySystemTemplate(companyGuid!, GUEST_ORDER_TEMPLATE_TYPE),
    onSuccess: () => {
      toast.success("업체 전용 템플릿을 삭제하고 기본으로 복귀했습니다.");
      qc.invalidateQueries({ queryKey: ["companySystemTemplate", companyGuid] });
    },
    onError: (err: Error) => {
      toast.error(`삭제 실패: ${err.message}`);
    },
  });
};
